import { CardServerError, CardSubmitResult, FormOption } from 'components/Alpha/cards/cardTypes';
import { LiveAssistCardRequired } from 'components/Alpha/LiveAssist/liveAssistContract';
import { cancelAlphaConfirmation, getAlphaCardOptions } from 'services/alpha';
import {
  SubmitEffect,
  TURN_IN_PROGRESS_MESSAGE,
  finishSubmit,
  initialSubmitState,
  submitOutcome,
} from 'services/alpha/cardSubmit';
import { streamAlpha } from 'services/alpha/sseClient';
import { ALPHA_STALLED_MESSAGE, ALPHA_UNREACHABLE_MESSAGE, AlphaEvent } from 'services/alpha/types';

export const CARD_NOT_SENT_TEXT = "Couldn't send that to Alpha. Try again.";
export const CARD_CANCEL_FAILED_TEXT = "Couldn't cancel that. Try again.";
const RATE_LIMITED_TEXT = "You're sending requests quickly. Wait a moment, then try again.";
const HEADERS_TIMEOUT_MS = 30000;
const SUBMIT_MAX_WAIT_MS = 30000;

export interface LiveAssistTurnCallbacks {
  replace: (next: LiveAssistCardRequired) => void;
  close: () => void;
  reply: (text: string) => void;
}

type CardRequiredEvent = Extract<AlphaEvent, { type: 'card_required' }>;

const toRequired = (event: CardRequiredEvent): LiveAssistCardRequired => ({
  kind: event.kind,
  toolName: event.toolName,
  token: event.token,
  card: event.card,
});

const sessionPath = (sessionId: number, action: string): string =>
  `/Alpha/chat/sessions/${sessionId}/${action}`;

const forward = (event: AlphaEvent, on: LiveAssistTurnCallbacks): void => {
  if (event.type === 'text_delta') on.reply(event.text);
  if (event.type === 'card_required') on.replace(toRequired(event));
};

export const submitLiveAssistCard = async (
  sessionId: number,
  token: string,
  values: Record<string, unknown>,
  on: LiveAssistTurnCallbacks,
): Promise<CardSubmitResult> => {
  let state = initialSubmitState(token);
  const apply = (effect: SubmitEffect): void => {
    if (effect.type === 'forward') forward(effect.event, on);
    if (effect.type === 'replaceCard') on.replace(toRequired(effect.event));
    if (effect.type === 'closeCard') on.close();
  };
  await streamAlpha(
    sessionPath(sessionId, 'cards/submit'),
    { token, values },
    event => {
      const step = submitOutcome(state, event);
      state = step.state;
      step.effects.forEach(apply);
    },
    undefined,
    {
      fetchReply: async () => false,
      maxWaitMs: SUBMIT_MAX_WAIT_MS,
      headersTimeoutMs: HEADERS_TIMEOUT_MS,
    },
  );
  const finished = finishSubmit(state, false);
  if (finished.reject) throw new Error(CARD_NOT_SENT_TEXT);
  return finished.result;
};

export type LiveAssistConfirmOutcome = { ok: true } | { ok: false; error: CardServerError };

const failureOf = (event: AlphaEvent): string | undefined => {
  switch (event.type) {
    case 'rate_limited':
      return RATE_LIMITED_TEXT;
    case 'turn_in_progress':
      return TURN_IN_PROGRESS_MESSAGE;
    case 'timeout':
      return ALPHA_STALLED_MESSAGE;
    case 'error':
      return event.origin === 'client' ? ALPHA_UNREACHABLE_MESSAGE : event.message;
    default:
      return undefined;
  }
};

const PROGRESS: Array<AlphaEvent['type']> = [
  'text_delta',
  'tool_call',
  'tool_result',
  'visual',
  'card_required',
  'confirmation_required',
];

export const confirmLiveAssistCard = async (
  sessionId: number,
  token: string,
  on: LiveAssistTurnCallbacks,
): Promise<LiveAssistConfirmOutcome> => {
  let progressed = false;
  let failure: string | undefined;
  let cardError: CardServerError | undefined;
  await streamAlpha(
    sessionPath(sessionId, 'confirm'),
    { token },
    event => {
      if (event.type === 'card_error' && event.token === token) {
        cardError = { message: event.message, fieldErrors: event.fieldErrors ?? {} };
        return;
      }
      progressed = progressed || PROGRESS.includes(event.type);
      failure = failureOf(event) ?? failure;
      forward(event, on);
    },
    undefined,
    { fetchReply: async () => false, headersTimeoutMs: HEADERS_TIMEOUT_MS },
  );
  if (cardError !== undefined) return { ok: false, error: cardError };
  if (failure !== undefined && !progressed) return { ok: false, error: { message: failure } };
  return { ok: true };
};

type CancelResult =
  | {
      ok?: boolean;
      alreadyResolved?: boolean;
      alreadyCancelled?: boolean;
      expired?: boolean;
      notFound?: boolean;
      message?: string;
    }
  | undefined;

export const CARD_CANCELLED_LABEL = 'Cancelled';

export const cancelLiveAssistCard = async (sessionId: number, token: string): Promise<string> => {
  let result: CancelResult;
  try {
    const res = await cancelAlphaConfirmation(sessionId, token);
    if (!res) throw new Error(CARD_CANCEL_FAILED_TEXT);
    result = res.data as CancelResult;
  } catch {
    throw new Error(CARD_CANCEL_FAILED_TEXT);
  }
  const terminal =
    result?.alreadyResolved === true || result?.expired === true || result?.notFound === true;
  if (result?.ok === false && !terminal) {
    throw new Error(CARD_CANCEL_FAILED_TEXT);
  }
  if (result?.ok === false && result.alreadyCancelled !== true) return result.message ?? '';
  return CARD_CANCELLED_LABEL;
};

export const cancelLiveAssistCardBeforeDismiss = async (
  sessionId: number,
  token: string,
): Promise<void> => {
  const res = await cancelAlphaConfirmation(sessionId, token).catch(() => undefined);
  if (!res) throw new Error(CARD_CANCEL_FAILED_TEXT);
  const result = res.data as CancelResult;
  const nothingLeft =
    result?.alreadyResolved === true || result?.expired === true || result?.notFound === true;
  if (result?.ok === false && !nothingLeft) throw new Error(CARD_CANCEL_FAILED_TEXT);
};

export const loadLiveAssistCardOptions = async (
  sessionId: number,
  token: string,
  source: string,
  query: string,
): Promise<FormOption[]> => {
  const res = await getAlphaCardOptions(sessionId, token, source, query);
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows.map(row => ({
    value: String(row.value),
    label: row.label,
    ...(typeof row.detail === 'string' && row.detail !== '' ? { detail: row.detail } : {}),
  }));
};
